// Angular modules
import { HttpClient }    from '@angular/common/http';
import { HttpHeaders }   from '@angular/common/http';
import { Injectable }    from '@angular/core';

// External modules
import { Category }      from '../models/category';
import { KeyPoint }      from '../models/keypoint';
import { Mixture }       from '../models/mixture';
import { Product }       from '../models/product';
import { ProductFamily } from '../models/productFamily';
import { Observable }    from 'rxjs/Rx';
import { of }            from 'rxjs/observable/of';
import { map }           from 'rxjs/operators';
import { catchError }    from 'rxjs/operators';

// Internal modules
import { environment }   from '../../../environments/environment';

// Interfaces
import { Image }         from '../interfaces/image.interface';
import { User }          from '../interfaces/user.interface';
import { StorageHelper } from '../helpers/storage.helper';
import { Contact } from '../models/contact';

@Injectable()
export class DataService
{
  user: User|null;

  constructor(private http: HttpClient)
  {
    this.user = StorageHelper.getUser();
  }

  async isServerAvailable(): Promise<boolean>
  {
    try
    {
      return this.http.get<boolean>(environment.API_ENDPOINT + '/health/ping').pipe(
        map(res => res == true),
        catchError(_ => of(false))
      ).toPromise();
    } catch (error)
    {
      return false;
    }
  }

  async getUserData(): Promise<User|null>
  {
    try
    {
      const options = { withCredentials: true };
      return this.http.get<User>(environment.API_ENDPOINT + `/user/currentdata`, options)
        .pipe(
          catchError(_ => of(null))
        ).toPromise()
        .catch(_ => null);
    }
    catch (error)
    {
      return Promise.resolve(null);
    }
  }

  async requestProducts(): Promise<Product[]|null>
  {
    if(this.user == null)
      return null;

    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'application/json', 'apiKey' : environment.API_KEY });
    const options    = { headers: headers, withCredentials: true };

    return this.http.get<Product[]|null>(environment.API_ENDPOINT + '/getActiveProducts/' + this.user.userId, options)
    .catch(_ => Observable.throw('The server is not available or maybe your are not authorized to get data'))
    .toPromise()
    .catch(_ => null);
  }

  async requestProductFamilies(): Promise<ProductFamily[]|null>
  {
    if(this.user == null)
      return null;

    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'application/json', 'apiKey' : environment.API_KEY });
    const options    = { headers: headers, withCredentials: true };

    return this.http.get<ProductFamily[]|null>(environment.API_ENDPOINT + '/getProductFamilies/' + this.user.userId, options)
      .toPromise()
      .catch(_ => null);
  }

  async requestContact(): Promise<Contact[]|null>
  {
    if(this.user == null)
      return null;

    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'application/json', 'apiKey' : environment.API_KEY });
    const options    = { headers: headers, withCredentials: true };

    return this.http.get<Contact[]|null>(environment.API_ENDPOINT + '/contacts/' + this.user.userId, options)
      .toPromise()
      .catch(_ => null);
  }

  async requestProductCategories(): Promise<Category[]|null>
  {
    if(this.user == null)
      return null;

    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'application/json', 'apiKey' : environment.API_KEY });
    const options    = { headers: headers, withCredentials: true };

    return this.http.get<Category[]|null>(environment.API_ENDPOINT + '/getProductCategories/' + this.user.userId, options)
      .toPromise()
      .catch(_ => null);
  }

  async requestKeyPoints(): Promise<KeyPoint[]|null>
  {
    if(this.user == null)
      return null;

    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'application/json', 'apiKey' : environment.API_KEY });
    const options    = { headers: headers, withCredentials: true };

    return this.http.get<KeyPoint[]|null>(environment.API_ENDPOINT + '/getKeyPoints/' + this.user.userId, options)
    .catch((error:any) => Observable.throw('The server is not available or maybe your are not authorized to get data'))
    .toPromise()
    .catch(_ => null);
  }

  async requestGasMixtures(): Promise<Mixture[]|null>
  {
    if(this.user == null)
      return null;

    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'application/json', 'apiKey' : environment.API_KEY });
    const options    = { headers: headers, withCredentials: true };

    return this.http.get<Mixture[]|null>(environment.API_ENDPOINT + '/getProductGasMixtures/' + this.user.userId, options)
    .catch((error:any) => Observable.throw('The server is not available or maybe your are not authorized to get data'))
    .toPromise()
    .catch(_ => null);
  }

  async requestImages(): Promise<Image[]|null>
  {
    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'text/plain', 'apiKey' : environment.API_KEY });
    const options    = {
      headers: headers,
      withCredentials: true,
    };

    return this.http.get<Image[]|null>(environment.API_ENDPOINT + '/images', options)
    .catch((error:any) => Observable.throw(`The server is not available or maybe your are not authorized to get data: ${JSON.stringify(error)}`))
    .toPromise()
    .catch(_ => null);
  }

  async requestImageContent(imageId: string): Promise<string|null>
  {
    const headers    = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept' : 'text/plain', 'apiKey' : environment.API_KEY });
    const options    = {
      headers: headers,
      withCredentials: true,
    };

    return this.http.get<string|null>(environment.API_ENDPOINT + '/images/content/' + imageId, options)
    .catch((error:any) => Observable.throw(`The server is not available or maybe your are not authorized to get data: ${JSON.stringify(error)}`))
    .toPromise()
    .catch(_ => null);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/providers/data-service.ts